import React, { useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn } from '../lib/utils';

const SearchInput = ({
  value,
  onChange,
  onSubmit,
  onClear,
  placeholder = 'Search...',
  className,
  inputClassName,
  buttonVariant = 'icon',
  disabled = false,
  inputProps = {},
  buttonClassName,
}) => {
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        onSubmit?.();
      }
    },
    [onSubmit]
  );

  const hasValue = Boolean(value && value.trim());
  const showInlineButton = buttonVariant === 'inline';
  const showClear = hasValue && onClear;
  const showSubmit = showInlineButton && onSubmit;
  const rightPaddingClass = showInlineButton && showClear ? 'pr-16' : (showInlineButton || showClear) ? 'pr-10' : '';

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="relative flex-1">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tg-text-muted)]" />
        <Input
          {...inputProps}
          value={value}
          onChange={(event) => onChange?.(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'pl-9',
            rightPaddingClass,
            inputClassName,
            inputProps?.className
          )}
        />
        {(showInlineButton || showClear) && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {showClear && (
              <button
                type="button"
                onClick={onClear}
                className="h-6 w-6 rounded-full flex items-center justify-center text-[var(--tg-text-muted)] hover:text-[var(--tg-text-primary)]"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            {showSubmit && (
              <button
                type="button"
                onClick={onSubmit}
                className="h-6 w-6 rounded-full flex items-center justify-center text-[var(--tg-text-muted)] hover:text-[var(--tg-text-primary)]"
                aria-label="Run search"
              >
                <Search className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
      {!showInlineButton && buttonVariant !== 'none' && (
        <Button
          type="button"
          variant="outline"
          size={buttonVariant === 'text' ? 'sm' : 'icon'}
          onClick={onSubmit}
          disabled={disabled}
          className={cn(
            buttonVariant === 'text' && 'rounded-full px-4',
            buttonVariant === 'icon' && 'h-10 w-10 rounded-full',
            buttonClassName
          )}
          aria-label="Run search"
        >
          <Search className="w-4 h-4" />
          {buttonVariant === 'text' && <span className="ml-2">Search</span>}
        </Button>
      )}
    </div>
  );
};

export default SearchInput;
